import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';

interface ReportRow {
    id: number;
    toolId: number;
    toolCode: string;
    toolName: string;
    type: string;
    title: string;
    status: string;
    reporterEmail?: string;
    createdAt: string;
}

interface ReportDetail {
    id: number;
    toolId: number;
    type: string;
    title: string;
    description?: string;
    status: string;
    reporterEmail?: string;
    reporterName?: string;
    createdAt: string;
    updatedAt?: string;
}

const TYPE_LABEL: Record<string, string> = {
    Bug: 'Bug',
    Improvement: 'Miglioramento',
    Question: 'Domanda',
};

const TYPE_ICON: Record<string, string> = {
    Bug: 'bi-bug',
    Improvement: 'bi-lightbulb',
    Question: 'bi-question-circle',
};

const STATUS_BADGE: Record<string, string> = {
    Open: 'bg-danger',
    InProgress: 'bg-warning text-dark',
    Resolved: 'bg-success',
    Closed: 'bg-secondary',
};

const STATUS_LABEL: Record<string, string> = {
    Open: 'Aperta',
    InProgress: 'In lavorazione',
    Resolved: 'Risolta',
    Closed: 'Chiusa',
};

const QualityReportsPage: React.FC = () => {
    const { showToast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();
    const [rows, setRows] = useState<ReportRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(() => searchParams.get('status') || 'Open');
    const [type, setType] = useState(() => searchParams.get('type') || 'all');
    const [toolCode, setToolCode] = useState(() => searchParams.get('toolCode') || '');
    const [search, setSearch] = useState('');
    const [updatingId, setUpdatingId] = useState<number | null>(null);
    const [detail, setDetail] = useState<ReportDetail | null>(null);
    const [detailRow, setDetailRow] = useState<ReportRow | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const toolId = Number(searchParams.get('toolId')) || null;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiService.adminGetReportsCrossTool<ReportRow>({
                status: status === 'all' ? undefined : status,
                type: type === 'all' ? undefined : type,
            });
            setRows(response);
        } catch {
            showToast('Errore', 'Impossibile caricare le segnalazioni', 'danger');
        } finally {
            setLoading(false);
        }
    }, [showToast, status, type]);

    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        const next = new URLSearchParams();
        if (status !== 'Open') next.set('status', status);
        if (type !== 'all') next.set('type', type);
        if (toolCode.trim()) next.set('toolCode', toolCode.trim());
        if (toolId) next.set('toolId', String(toolId));
        setSearchParams(next, { replace: true });
    }, [setSearchParams, status, toolCode, toolId, type]);

    const filteredRows = useMemo(() => {
        const query = search.trim().toLocaleLowerCase();
        return rows.filter(row => {
            if (toolId && row.toolId !== toolId) return false;
            if (toolCode.trim() && row.toolCode.toLocaleLowerCase() !== toolCode.trim().toLocaleLowerCase()) return false;
            return !query
                || row.title.toLocaleLowerCase().includes(query)
                || row.toolName.toLocaleLowerCase().includes(query)
                || (row.reporterEmail ?? '').toLocaleLowerCase().includes(query);
        });
    }, [rows, search, toolCode, toolId]);

    const updateStatus = async (row: ReportRow, nextStatus: string) => {
        if (row.status === nextStatus) return;
        setUpdatingId(row.id);
        try {
            await apiService.adminUpdateReportStatus(row.toolId, row.id, nextStatus);
            if (status !== 'all' && status !== nextStatus) {
                setRows(current => current.filter(item => item.id !== row.id));
            } else {
                setRows(current => current.map(item => item.id === row.id ? { ...item, status: nextStatus } : item));
            }
            setDetail(current => current?.id === row.id ? { ...current, status: nextStatus } : current);
            setDetailRow(current => current?.id === row.id ? { ...current, status: nextStatus } : current);
            showToast('Stato aggiornato', STATUS_LABEL[nextStatus] ?? nextStatus, 'success');
        } catch {
            showToast('Errore', 'Stato della segnalazione non aggiornato', 'danger');
        } finally {
            setUpdatingId(null);
        }
    };

    const openDetail = async (row: ReportRow) => {
        setDetailRow(row);
        setDetail(null);
        setDetailLoading(true);
        try {
            const toolReports = await apiService.adminGetToolReports<ReportDetail>(row.toolId);
            setDetail(toolReports.find(item => item.id === row.id) ?? null);
        } catch {
            showToast('Errore', 'Dettaglio della segnalazione non disponibile', 'danger');
        } finally {
            setDetailLoading(false);
        }
    };

    return (
        <>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h5 className="mb-1">Segnalazioni utenti</h5>
                    <div className="small text-body-secondary">
                        Bug, richieste di miglioramento e domande provenienti da tutti i tool.
                    </div>
                </div>
                <button className="btn btn-sm btn-outline-primary" onClick={load} disabled={loading}>
                    <i className="bi bi-arrow-clockwise me-1" />{loading ? 'Aggiorno…' : 'Aggiorna'}
                </button>
            </div>

            <div className="card shadow-sm mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Stato</label>
                            <select className="form-select form-select-sm" value={status}
                                onChange={event => setStatus(event.target.value)}>
                                <option value="all">Tutti</option>
                                <option value="Open">Aperte</option>
                                <option value="InProgress">In lavorazione</option>
                                <option value="Resolved">Risolte</option>
                                <option value="Closed">Chiuse</option>
                            </select>
                        </div>
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Tipo</label>
                            <select className="form-select form-select-sm" value={type}
                                onChange={event => setType(event.target.value)}>
                                <option value="all">Tutti</option>
                                <option value="Bug">Bug</option>
                                <option value="Improvement">Miglioramento</option>
                                <option value="Question">Domanda</option>
                            </select>
                        </div>
                        <div className="col-12 col-md-3">
                            <label className="form-label small">Tool code (esatto)</label>
                            <input className="form-control form-control-sm" value={toolCode}
                                onChange={event => setToolCode(event.target.value)} placeholder="Tutti i tool" />
                        </div>
                        <div className="col-12 col-md">
                            <label className="form-label small">Cerca</label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text"><i className="bi bi-search" /></span>
                                <input className="form-control" value={search}
                                    onChange={event => setSearch(event.target.value)} placeholder="Titolo, tool o utente" />
                            </div>
                        </div>
                    </div>
                    {(toolId || toolCode) && (
                        <div className="small text-body-secondary mt-2">
                            Vista filtrata per tool.{' '}
                            <button type="button" className="btn btn-link btn-sm p-0 align-baseline"
                                onClick={() => { setToolCode(''); setSearchParams({ status }); }}>
                                Rimuovi filtro
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="card shadow-sm">
                <div className="card-header bg-body-tertiary d-flex justify-content-between align-items-center">
                    <strong>{filteredRows.length} segnalazioni</strong>
                    <span className="small text-body-secondary">Massimo 500 risultati</span>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Tool</th>
                                <th>Tipo</th>
                                <th>Segnalazione</th>
                                <th>Stato</th>
                                <th>Da</th>
                                <th>Quando</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={7} className="text-center text-body-secondary p-4">
                                    <span className="spinner-border spinner-border-sm me-2" />Caricamento…
                                </td></tr>
                            )}
                            {!loading && filteredRows.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-body-secondary p-4">
                                    Nessuna segnalazione corrisponde ai filtri.
                                </td></tr>
                            )}
                            {!loading && filteredRows.map(row => (
                                <tr key={row.id}>
                                    <td>
                                        <div className="small fw-semibold">{row.toolName}</div>
                                        <code className="small text-body-secondary">{row.toolCode}</code>
                                    </td>
                                    <td>
                                        <span className="small"><i className={`bi ${TYPE_ICON[row.type] ?? 'bi-flag'} me-1`} />
                                            {TYPE_LABEL[row.type] ?? row.type}
                                        </span>
                                    </td>
                                    <td style={{ minWidth: 240 }}>
                                        <button type="button" className="btn btn-link p-0 text-start text-decoration-none fw-semibold"
                                            onClick={() => void openDetail(row)}>
                                            {row.title}
                                        </button>
                                    </td>
                                    <td>
                                        <select className="form-select form-select-sm" style={{ minWidth: 145 }}
                                            value={row.status} disabled={updatingId === row.id}
                                            onChange={event => void updateStatus(row, event.target.value)}>
                                            {Object.entries(STATUS_LABEL).map(([value, label]) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="small">{row.reporterEmail || '—'}</td>
                                    <td className="small text-body-secondary" style={{ whiteSpace: 'nowrap' }}>
                                        {new Date(row.createdAt).toLocaleString('it-IT')}
                                    </td>
                                    <td>
                                        <div className="d-flex gap-1">
                                            <button className="btn btn-sm btn-outline-primary" onClick={() => void openDetail(row)}
                                                aria-label="Apri dettaglio">
                                                <i className="bi bi-eye" />
                                            </button>
                                            <Link className="btn btn-sm btn-outline-secondary" to={`/tools/${row.toolId}`}
                                                aria-label="Apri configurazione tool">
                                                <i className="bi bi-tools" />
                                            </Link>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal show={detailRow !== null} onHide={() => { setDetailRow(null); setDetail(null); }} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Dettaglio segnalazione</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailLoading && <div className="text-center p-4"><span className="spinner-border" /></div>}
                    {!detailLoading && detailRow && (
                        <>
                            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                                <span className="badge bg-light text-dark border">{TYPE_LABEL[detailRow.type] ?? detailRow.type}</span>
                                <span className={`badge ${STATUS_BADGE[detail?.status ?? detailRow.status] ?? 'bg-secondary'}`}>
                                    {STATUS_LABEL[detail?.status ?? detailRow.status] ?? (detail?.status ?? detailRow.status)}
                                </span>
                                <span className="small text-body-secondary ms-auto">
                                    {new Date(detailRow.createdAt).toLocaleString('it-IT')}
                                </span>
                            </div>
                            <h5>{detail?.title ?? detailRow.title}</h5>
                            <div className="small text-body-secondary mb-3">
                                {detailRow.toolName} (<code>{detailRow.toolCode}</code>) ·{' '}
                                {detail?.reporterName || detail?.reporterEmail || detailRow.reporterEmail || 'Utente non disponibile'}
                            </div>
                            <div className="border rounded bg-body-tertiary p-3" style={{ whiteSpace: 'pre-wrap', minHeight: 100 }}>
                                {detail?.description || <span className="text-body-secondary">Nessuna descrizione aggiuntiva.</span>}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {detailRow && (
                        <Link className="btn btn-outline-secondary" to={`/tools/${detailRow.toolId}`}>Configura tool</Link>
                    )}
                    <Button variant="secondary" onClick={() => { setDetailRow(null); setDetail(null); }}>Chiudi</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default QualityReportsPage;
